import React from "react";
import { useAuth } from "../auth/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";

const WARNING_WINDOW_SECONDS = 5 * 60;

function formatRemaining(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  const total = Math.max(0, seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

export default function Topbar({ onToggleSidebar }) {
  const { user, token, logout, refreshSession, sessionExpiresAt } = useAuth();
  const [now, setNow] = React.useState(Date.now());
  const [warningDismissedForToken, setWarningDismissedForToken] = React.useState(null);
  const [warningOpen, setWarningOpen] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    if (!sessionExpiresAt) return undefined;
    setNow(Date.now());
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [sessionExpiresAt]);

  const remainingSeconds = sessionExpiresAt ? Math.max(0, Math.ceil((sessionExpiresAt - now) / 1000)) : null;
  const isExpiringSoon = remainingSeconds !== null && remainingSeconds <= WARNING_WINDOW_SECONDS;

  React.useEffect(() => {
    if (!token || !isExpiringSoon || remainingSeconds === 0) {
      setWarningOpen(false);
      return;
    }
    if (warningDismissedForToken === token) return;
    setWarningOpen(true);
  }, [token, isExpiringSoon, remainingSeconds, warningDismissedForToken]);

  const handleWarningOpenChange = (open) => {
    setWarningOpen(open);
    if (!open && token) {
      setWarningDismissedForToken(token);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshSession();
      setWarningDismissedForToken(null);
      setWarningOpen(false);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <header className="h-12 bg-white border-b shadow-soft flex items-center px-4 sticky top-0 z-30">
      <button
        onClick={onToggleSidebar}
        className="mr-3 rounded p-1 hover:bg-slate-100"
        aria-label="Toggle sidebar"
      >
        ☰
      </button>
      <div className="flex items-center gap-2 font-semibold tracking-wide text-slate-900">
        <img src="/pvapplogo.png" alt="PV-report logo" className="h-8 w-8 rounded" />
        <span>PV-report</span>
      </div>
      <div className="ml-auto flex items-center gap-3">
        {user && (
          <>
            {remainingSeconds !== null ? (
              <div
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  isExpiringSoon ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                }`}
                title="Remaining session time"
              >
                Session {formatRemaining(remainingSeconds)}
              </div>
            ) : null}
            {isExpiringSoon ? (
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {refreshing ? "Extending..." : "Extend session"}
              </button>
            ) : null}
            <span className="text-sm text-slate-600">{user.user_email}</span>
            <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 uppercase">
              {user.user_role}
            </span>
            <button
              onClick={logout}
              className="text-sm text-slate-600 hover:text-slate-900 underline"
            >
              Logout
            </button>
          </>
        )}
      </div>
      <Dialog open={warningOpen} onOpenChange={handleWarningOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Session expiring soon</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-600">
            <p>Your session expires in {formatRemaining(remainingSeconds)}.</p>
            <p>Extend session to stay signed in without interrupting your work.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => handleWarningOpenChange(false)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
              >
                Dismiss
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="rounded-lg bg-slate-900 px-4 py-2 font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {refreshing ? "Extending..." : "Extend session"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
